import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import PageStatus from '../../PageStatus/PageStatus';
import OutletSearch from '../../SelectSearch/OutletSearch';
import CentralWastageInput from './CentralWastageInput';
import CentralWastageTable from './CentralWastageTable';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "34px",
        height: "34px",
        fontSize: "0.875rem",
        borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
        boxShadow: "none",
        "&:hover": { borderColor: "#3b82f6" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "34px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 6px" }),
    menu: (base) => ({ ...base, zIndex: 50, fontSize: "0.875rem" }),
    option: (base, { isFocused }) => ({
        ...base,
        backgroundColor: isFocused ? "#EFF6FF" : "white",
        color: "#111827",
    }),
};

const readonlyClass =
    "w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed";

const editableClass =
    "w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500";

const CentralKitchenWastage = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const [selectedOutlet, setSelectedOutlet] = useState();

    const [stockItems, setStockItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [selectedDate, setSelectedDate] = useState(
        new Date().toLocaleDateString('en-CA')
    );

    const [selectedItem, setSelectedItem] = useState(null);

    const [mainQuantity, setMainQuantity] = useState("");
    const [subQuantity, setSubQuantity] = useState("");

    const [wastageItems, setWastageItems] = useState([]);

    const [costCenter, setCostCenter] = useState("Central Kitchen");

    const [isLoading, setIsLoading] = useState(false);

    const [editingItemId, setEditingItemId] = useState(null);

    const fetchStockGroups = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${baseUrl}/getcentralkitchenitems/filter?outlet=${decodeURI(
                    selectedOutlet
                )}`
            );

            setStockItems(response.data?.items || []);

        } catch (error) {
            console.error("Error fetching stock groups:", error);

            setError("Error fetching stock items");

            toast.error(
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Error fetching stock items"
            );

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedOutlet) return;

        fetchStockGroups();

    }, [selectedOutlet, baseUrl]);

    useEffect(() => {
        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
        setWastageItems([]);
    }, [selectedOutlet]);

    const getStockItemByName = (itemName) => {
        return stockItems.find(stock => stock.item_name === itemName);
    };

    const hasSecondaryUnit = (stockItem) => {
        return (
            stockItem?.unit_definition?.uom &&
            stockItem?.uom?.toLowerCase() !==
            stockItem?.unit_definition?.uom?.toLowerCase()
        );
    };

    const handleAddItem = () => {
        if (!selectedItem) {
            toast.error("Please select an item");
            return;
        }

        const parsedMain = parseFloat(mainQuantity);
        const parsedSub = parseFloat(subQuantity);

        if (
            (isNaN(parsedMain) || parsedMain <= 0) &&
            (isNaN(parsedSub) || parsedSub <= 0)
        ) {
            toast.error("Enter valid quantity");
            return;
        }

        const alreadyExists = wastageItems.find(
            item => item.id === selectedItem.id
        );

        if (alreadyExists && !editingItemId) {
            toast.warn("Item already added. Please edit instead.");
            return;
        }

        const newItem = {
            id: selectedItem.id,
            item_name: selectedItem.name,
            mainQty: parsedMain || 0,
            subQty: parsedSub || 0,
            uom: selectedItem.uom,
            secondaryUom: selectedItem.secondaryUom
        };

        if (editingItemId) {
            setWastageItems(prev =>
                prev.map(item =>
                    item.id === editingItemId ? newItem : item
                )
            );

            toast.success("Item updated successfully");

        } else {
            setWastageItems(prev => [...prev, newItem]);
        }

        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleEditItem = (item) => {
        const stockItem = getStockItemByName(item.item_name);

        if (!stockItem) {
            toast.error("Item not found");
            return;
        }

        setSelectedItem({
            id: stockItem.item_name,
            name: stockItem.item_name,
            uom: stockItem.uom,
            secondaryUom: stockItem.unit_definition?.uom || ''
        });

        setMainQuantity(item.mainQty?.toString() || "");
        setSubQuantity(item.subQty?.toString() || "");
        setEditingItemId(item.id);
    };

    const handleCancelEdit = () => {
        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleRemoveItem = (id) => {
        setWastageItems(
            wastageItems.filter(item => item.id !== id)
        );

        if (editingItemId === id) {
            handleCancelEdit();
        }
    };

    const computeWastageQuantity = (item, stockItem) => {
        const mainQty = parseFloat(item.mainQty) || 0;
        const subQty = parseFloat(item.subQty) || 0;

        const unitSize = parseFloat(stockItem?.unit_definition?.unit_value) || 1;
        const baseUom = stockItem?.unit_definition?.uom?.toLowerCase();
        const mainUom = stockItem?.uom?.toLowerCase();

        if (!subQty || mainUom === baseUom) {
            return mainQty;
        }

        return mainQty + (subQty / unitSize);
    };

    const handleSubmit = () => {
        if (!token || !selectedOutlet) {
            toast.error("Please select an outlet");
            return;
        }

        if (wastageItems.length === 0) {
            toast.error("Please add at least one item");
            return;
        }

        if (!costCenter) {
            toast.error("Please enter cost center");
            return;
        }

        if (!selectedDate) {
            toast.error("Please select a date");
            return;
        }

        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">
                    Are you sure you want to save these wastage stocks?
                </p>

                <div className="flex gap-2">
                    <button
                        className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                        onClick={async () => {
                            toast.dismiss();

                            setIsLoading(true);

                            try {
                                const payload = {
                                    token,
                                    outlet_name: selectedOutlet,
                                    costcenter: costCenter.value || costCenter,
                                    received_date: selectedDate,

                                    items: wastageItems.map(item => {
                                        const stockItem =
                                            getStockItemByName(item.item_name);

                                        const finalQty =
                                            computeWastageQuantity(
                                                item,
                                                stockItem
                                            );

                                        return {
                                            item_name: item.item_name,
                                            quantity: finalQty,
                                            uom: stockItem?.uom
                                        };
                                    })
                                };

                                await axios.post(
                                    `${baseUrl}/wastage-items`,
                                    payload
                                );

                                toast.success(
                                    "Wastage added successfully!"
                                );

                                setSelectedDate(
                                    new Date().toLocaleDateString('en-CA')
                                );

                                setWastageItems([]);

                                setCostCenter("Central Kitchen");

                                setEditingItemId(null);

                            } catch (error) {
                                console.error(
                                    'Error submitting wastage:',
                                    error
                                );

                                toast.error(
                                    error?.response?.data?.error ||
                                    error?.response?.data?.message ||
                                    'Error submitting wastage'
                                );

                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>

                    <button
                        className="px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-wastage-stocks'
            }
        );
    };

    const itemOptions = stockItems?.map(stock => ({
        value: stock.item_name,
        label: stock.item_name,
        data: {
            id: stock.item_name,
            name: stock.item_name,
            uom: stock.uom,
            secondaryUom: stock.unit_definition?.uom || ''
        }
    }));

    const currentStockItem = selectedItem
        ? getStockItemByName(selectedItem.id)
        : null;

    const showSubUnitInput =
        currentStockItem &&
        hasSecondaryUnit(currentStockItem);

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl mt-2 mb-5 mx-auto">
                    <div className="bg-white rounded-lg shadow-md p-4">

                        <div className='flex justify-center mb-3'>
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>

                        <h2 className="text-base font-semibold mb-2">
                            Production Items Wastage
                        </h2>

                        <PageStatus
                            loading={loading}
                            error={error}
                            loadingText="Loading production items..."
                        />

                        {!loading && !error && (
                            <>
                                <CentralWastageInput
                                    editableClass={editableClass}
                                    readonlyClass={readonlyClass}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    costCenter={costCenter}
                                    selectStyles={selectStyles}
                                    selectedOutlet={selectedOutlet}
                                    itemOptions={itemOptions}
                                    selectedItem={selectedItem}
                                    setSelectedItem={setSelectedItem}
                                    mainQuantity={mainQuantity}
                                    setMainQuantity={setMainQuantity}
                                    subQuantity={subQuantity}
                                    setSubQuantity={setSubQuantity}
                                    showSubUnitInput={showSubUnitInput}
                                    editingItemId={editingItemId}
                                    handleAddItem={handleAddItem}
                                    handleCancelEdit={handleCancelEdit}
                                />

                                <CentralWastageTable
                                    wastageItems={wastageItems}
                                    getStockItemByName={getStockItemByName}
                                    hasSecondaryUnit={hasSecondaryUnit}
                                    editingItemId={editingItemId}
                                    handleEditItem={handleEditItem}
                                    handleRemoveItem={handleRemoveItem}
                                    handleSubmit={handleSubmit}
                                    isLoading={isLoading}
                                />
                            </>
                        )}

                    </div>
                </div>
            </div>
        </div>
    )
}

export default CentralKitchenWastage